import {useContext, useMemo} from 'react';
import {Switch} from 'react-router-dom';

import {UserContext} from 'context';
import {RedirectToPrivateRoute, PrivateRoute, ROUTE_CONFIG} from 'utils';

import {Appointments} from './Appointments';
import {Billing} from './Billing';
import {ClientsConnects} from './ClientsConnects';
import {GroupsManagement} from './GroupsManagement';
import {Integrations} from './Integrations/Integrations';
import {LiveHelp} from './LiveHelp';
import {OutgoingMessages} from './OutgoingMessages';
import {Profile} from './Profile';
import {Roles} from './Roles';
import {AccountSession} from './Session';
import {Settings} from './Settings';
import {Users} from './Users';
import {WorkingHours} from './WorkingHours';

export const Account: React.FC = () => {
    const {settings} = useContext(UserContext);
    const clientConnectionsEnabled = useMemo(() =>
        settings.featuresAvailability.clientsConnections, [settings]);

    const workingHoursEnabled = useMemo(() =>
        settings.featuresAvailability.workingHours, [settings]);

    const createAgentWithPasswordAllowed = useMemo(() =>
        settings.registration.withPassword, [settings]);

    const createAgentWithEmailConfirmationAllowed = useMemo(() =>
        settings.registration.withEmailConfirmation, [settings]);

    const outgoingMessagesEnabled = useMemo(() =>
        settings.featuresAvailability.outgoingMessages, [settings]);

    const canCreateAgent = useMemo(() =>
        createAgentWithPasswordAllowed || createAgentWithEmailConfirmationAllowed,
    [createAgentWithPasswordAllowed, createAgentWithEmailConfirmationAllowed]);

    return (
        <Switch>
            <PrivateRoute
                {...ROUTE_CONFIG.ACCOUNT.MY_PROFILE}
                render={(props) => (
                    <Profile {...props} isPersonalProfile />
                )}
            />
            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.SETTINGS} component={Settings} />
            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.INTEGRATION} component={Integrations} />
            {canCreateAgent &&
                <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.CREATE_USER} component={Profile} />}

            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.EDIT_USER} component={Profile} />
            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.USERS} component={Users} />
            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.GROUPS} component={GroupsManagement} />
            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.ROLES} component={Roles} />
            {workingHoursEnabled && <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.WORKING_HOURS} component={WorkingHours} />}

            {clientConnectionsEnabled &&
                <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.CLIENTS_CONNECTS} component={ClientsConnects} />}
            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.LIVE_HELP} component={LiveHelp} />

            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.SESSION} component={AccountSession} />
            {outgoingMessagesEnabled &&
                <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.OUTGOING_MESSAGES} component={OutgoingMessages} />}

            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.APPOINTMENTS} component={Appointments} />

            <PrivateRoute {...ROUTE_CONFIG.ACCOUNT.BILLING} component={Billing} />

            <RedirectToPrivateRoute
                routes={[
                    ROUTE_CONFIG.ACCOUNT.MY_PROFILE,
                    ROUTE_CONFIG.ACCOUNT.SETTINGS,
                    ROUTE_CONFIG.ACCOUNT.INTEGRATION,
                    ROUTE_CONFIG.ACCOUNT.USERS,
                    ROUTE_CONFIG.ACCOUNT.GROUPS,
                    ROUTE_CONFIG.ACCOUNT.BILLING,
                    ROUTE_CONFIG.ACCOUNT.CLIENTS_CONNECTS,
                    ROUTE_CONFIG.ACCOUNT.ROLES,
                    ROUTE_CONFIG.ACCOUNT.WORKING_HOURS,
                    ROUTE_CONFIG.ACCOUNT.LIVE_HELP,
                    ROUTE_CONFIG.ACCOUNT.SESSION,
                    ROUTE_CONFIG.ACCOUNT.OUTGOING_MESSAGES,
                    ROUTE_CONFIG.ACCOUNT.APPOINTMENTS
                ]}
            />
        </Switch>
    );
};
